import { AsyncDropdownList } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import type { IGeocodeResult } from "../api/mapController";

import { MapController } from "../api/mapController";
import { QUERY_KEYS } from "../api/queries";
import { useApiWrapper } from "../api/useApiHook";

interface Value {
	value: string;
	label: string;
}

interface Props {
	value: IGeocodeResult;
	changeHandler: (value: IGeocodeResult, id: string) => void;
	label?: string;
	placeholder?: string;
	id?: string;
}

export function AddressDropdown(props: Props) {
	const { changeHandler, id = "address", label, placeholder } = props;
	const useApi = useApiWrapper();
	const [query, setQuery] = useState<Value | undefined>(undefined);

	const loadOptions = useCallback(
		async (inputValue: string) => {
			const values = await useApi(MapController.autocomplete(inputValue));

			return values?.map((item) => ({ value: item, label: item })) ?? [];
		},
		[useApi],
	);

	const geocodeQuery = useQuery({
		queryKey: [QUERY_KEYS.Components.Geocode, query],
		queryFn: () => MapController.geocode(query.value),
		enabled: !!query && query.value.length > 2,
	});

	useEffect(() => {
		if (geocodeQuery.data != null) {
			changeHandler(geocodeQuery.data, id);
		}
	}, [geocodeQuery.data, id, changeHandler]);

	const handleChange = useCallback(
		(value: Value | undefined) => {
			setQuery(value);
			// When the selection is cleared, reset the associated filter so all pins show again.
			if (!value) {
				changeHandler(undefined, id);
			}
		},
		[id, changeHandler],
	);

	return (
		<AsyncDropdownList
			id={id}
			loadOptions={loadOptions}
			value={query}
			changeHandler={handleChange}
			fullLength
			label={label}
			placeholder={placeholder}
		/>
	);
}
